// The mutation behind the row-level priority toggle.
//
// Shared by the chronological list and the priority board so the two cannot
// drift — they are the only callers, and the server rules they depend on
// (shared/flag-state.ts) are identical for both.
//
// WHAT THE SERVER DOES WITH `{ isFlagged: true }`, AND WHY THAT IS ENOUGH.
// resolveFlagState treats a false→true transition as a NEW flag: it sets
// flag_source='manual' and appends the RFQ to the bottom of the board. So this
// sends ONLY isFlagged and lets the server derive the rest. Sending
// flagSource:'manual' would also work, but it means "promote this auto-flag",
// and using the promotion path for an ordinary flag would blur two cases the
// server goes out of its way to tell apart.
//
// All cache reasoning lives in rfq-flag-cache.ts, where it can be tested against
// a real QueryClient without React. This file is deliberately only the wiring.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '@/lib/api';
import {
  applyOptimisticFlag,
  restoreRfqCaches,
  RFQ_QUERY_PREFIX,
} from '@/lib/rfq-flag-cache';

export { BOARD_QUERY_KEY } from '@/lib/rfq-flag-cache';

export function useToggleRfqFlag() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { id: string; isFlagged: boolean }) =>
      api(`/api/rfqs/${vars.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ isFlagged: vars.isFlagged }),
      }),

    onMutate: async ({ id, isFlagged }) => {
      // Cancel in-flight reads FIRST: a refetch that started before this click
      // would land after the optimistic write and silently undo it.
      await queryClient.cancelQueries({ queryKey: RFQ_QUERY_PREFIX });
      return { previous: applyOptimisticFlag(queryClient, id, isFlagged) };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) restoreRfqCaches(queryClient, context.previous);
    },

    // Reconcile with the server either way. This is what gives a newly flagged
    // RFQ its real board rank — the one value the optimistic path deliberately
    // does not guess, since the server computes it by appending.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: RFQ_QUERY_PREFIX });
    },
  });
}
